import React, { useState, useEffect } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Popper, Button, TextField, IconButton, Menu, MenuItem, useMediaQuery } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from "@mui/material/styles";
import favicon from './assets/Images/favicon.png';
import format from "date-fns/format";
import calender from "./assets/Images/calender.png";
import ArrowDropDownTwoToneIcon from '@mui/icons-material/ArrowDropDownTwoTone';
import axios from "axios";

const StyledButton = styled(Button)({
  fontFamily: "Caladea, serif",
  fontWeight: "bold",
  backgroundColor: "#ffcc00",
  color: "black",
  "&:hover": {
    backgroundColor: "#ffb300",
  },
});

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const StyledTableCell = styled(TableCell)({
  fontFamily: "Caladea, serif",
  fontWeight: "bold",
  backgroundColor: "#222",
  color: "#fff",
});

const ProductTable = ({ onSelectProduct }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // Add search term state
  const [selectedCategory, setSelectedCategory] = useState("View All");
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [monthAnchorEl, setMonthAnchorEl] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTab = useMediaQuery("(max-width:1024px)");
  const isMobileOrTab = isMobile || isTab;

  useEffect(() => {
    // Fetch products from the database
    const fetchProducts = async () => {
      try {
        const response = await axios.get("https://enzo-matr.onrender.com/api/products");
        setProducts(response.data);
        setFilteredProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  // Add this filter function
  useEffect(() => {
    if (!searchTerm) {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(
        products.filter(
          (item) =>
            (item.company && item.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (item.date && item.date.includes(searchTerm))
        )
      );
    }
  }, [searchTerm, products]);

  const handleDateClick = (event) => {
    if (dateAnchorEl) {
      setDateAnchorEl(null);
    } else {
      setDateAnchorEl(event.currentTarget);
    }
  };

  const handleMonthClick = (event) => {
    setMonthAnchorEl(monthAnchorEl ? null : event.currentTarget);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setSelectedMonth(null); // Clear month filter when date is picked
    setDateAnchorEl(null);
  };

  const handleMonthClose = (month) => {
    setMonthAnchorEl(null);
    if (month) {
      setSelectedMonth(month);
    }
  };

  const filteredData = filteredProducts.filter((item) => {
    const matchCategory = selectedCategory === "View All" || item.category === selectedCategory;
    const matchDate = !selectedDate || item.date === format(selectedDate, "yyyy-MM-dd");
    const matchMonth = !selectedMonth || new Date(item.date).toLocaleString('default', { month: 'long' }) === selectedMonth;
    return matchCategory && matchDate && matchMonth;
  });

  // Group products by date and company
  const groupedRows = React.useMemo(() => {
    const groups = {};
    filteredData.forEach((item) => {
      const key = `${item.date}__${item.company}`;
      if (!groups[key]) {
        groups[key] = {
          date: item.date,
          company: item.company,
          count: 0,
          products: [],
        };
      }
      groups[key].count += 1;
      groups[key].products.push(item);
    });
    return Object.values(groups);
  }, [filteredData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          width: isMobileOrTab ? "98vw" : "860px",
          maxHeight: isMobileOrTab ? "none" : "430px",
          padding: isMobileOrTab ? "8px" : "20px",
          boxShadow: 3,
          ml: isMobileOrTab ? 0 : "5px",
          borderRadius: 2,
          bgcolor: "white",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
          <TextField
            placeholder="Search by Company or Date"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: "390px" }}
            InputProps={{
              sx: { borderRadius: "10px", border: "1px solid" }
            }}
          />
          <IconButton sx={{ background: "black", color: "white", borderRadius: "10px", height: "42px" }}>
            <SearchIcon />
          </IconButton>
          <StyledButton
            onClick={() => {
              setSelectedCategory("View All");
              setSelectedDate(null);
              setSelectedMonth(null);
              setSearchTerm("");
            }}
            sx={{ bgcolor: "#333", fontSize: "20px", color: "white", textTransform: "none", width: "120px", height: '42px', borderRadius: "10px" }}
          >
            View All
          </StyledButton>
          <StyledButton onClick={handleMonthClick} sx={{ textTransform: "none", height: "42px", width: "120px", fontSize: '20px', borderRadius: '10px' }} endIcon={<ArrowDropDownTwoToneIcon sx={{ fontSize: "20px" }} />}>
            {selectedMonth ? selectedMonth : "Month"}
          </StyledButton>
          <Menu anchorEl={monthAnchorEl} open={Boolean(monthAnchorEl)} onClose={() => handleMonthClose(null)}>
            {months.map((month, index) => (
              <MenuItem key={index} onClick={() => handleMonthClose(month)}>
                {month}
              </MenuItem>
            ))}
          </Menu>
          <StyledButton onClick={handleDateClick}
            sx={{
              textTransform: "none",
              height: "42px",
              width: "120px",
              fontSize: "20px",
              borderRadius: "10px"
            }}
            endIcon={
              <img src={calender} alt="calander" height={25} />
            }
          >Date</StyledButton>
          <Popper open={Boolean(dateAnchorEl)} anchorEl={dateAnchorEl} placement="bottom-start">
            <Paper sx={{ p: 1 }}>
              <DateCalendar onChange={handleDateChange} />
              <Button
                size="small"
                onClick={() => {
                  setSelectedDate(null);
                  setDateAnchorEl(null);
                }}
                sx={{ mt: 1 }}
              >
                Clear Date
              </Button>
            </Paper>
          </Popper>
        </Box>
        <Box component={Paper} sx={{ overflow: "hidden", borderRadius: 2, mb: 1, height: "50px" }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Added Date", "Company Name", "Imported product", "View"].map((header, index) => (
                  <StyledTableCell key={index} sx={{ fontSize: '12px', fontFamily: "'Bricolage Grotesque',sans-serif" }}>{header}</StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: isMobileOrTab ? "60vh" : "320px",
            minHeight: isMobileOrTab ? "200px" : "320px",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table sx={{ minWidth: "100%" }} stickyHeader>
            <TableBody>
              {groupedRows.length > 0 ? (
                groupedRows.map((row, index) => (
                  <TableRow key={index} sx={{ borderBottom: "1px solid" }}>
                    <TableCell sx={{ fontSize: '12px', fontFamily: "'Bricolage Grotesque', sans-serif" }}>{row.date}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: "center", fontFamily: "'Bricolage Grotesque', sans-serif", position: "relative", right: "150px", }}>
                      {row.company}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', fontFamily: "'Bricolage Grotesque', sans-serif", position: 'relative', right: "30px" }}>{row.count}</TableCell>
                    <TableCell sx={{ textAlign: "center", fontFamily: "'Bricolage Grotesque', sans-serif", position: 'relative', left: "20px" }}>
                      <IconButton
                        onClick={() => onSelectProduct(row.products)} // Pass all products for that date/company
                        sx={{
                          background: "#FFC107",
                          width: "30px",
                          height: "30px",
                          borderRadius: "5px",
                          '&:hover': { background: "black" }
                        }}>
                        <img src={favicon} alt="Favicon" style={{ width: "35px", height: "30px" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No products available for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductTable;
